<template>
  <BulkActionsBar
    v-if="canEditTasks || !isArchivedView"
    :countSelected="countSelected"
    @clear:selection="handleClearSelection"
  >
    <template v-if="!isArchivedView" #actions>
      <ActionsModal
        :buildingIds="linkedBuildingIds"
        :steps="steps"
        :hints="hints"
        @submit="handleSubmit"
      >
        <ActionsButton
          :disabled="!hasActions"
          :disabledType="canEditTasks ? 'default' : 'limited_access'"
        />
      </ActionsModal>
    </template>
    <template #quick-actions>
      <TaskPrimaryActionButton
        :tasks="selectedTasks"
        :isArchivedView="isArchivedView"
        :canComplete="canCompleteTasks"
        :disabled="busy"
        @click="handleUpdateStatus"
      />
      <DeleteModal
        v-if="canEditTasks && !isArchivedView"
        :title="$t('components.task_details.delete.title', 2)"
        :description="$t('components.task_details.delete.description', 2)"
        :confirmLabel="$t('components.task_details.delete.button', 2)"
        @delete="handleUpdateStatus('delete')"
      >
        <DeleteButton />
      </DeleteModal>
    </template>
  </BulkActionsBar>
</template>

<script setup>
import route from "ziggy-js";
import { router } from "@inertiajs/vue3";
import { computed, inject, ref } from "vue";
import { storeToRefs } from "pinia";
import {
  ActionsButton,
  ActionsModal,
  BulkActionsBar,
  DeleteButton,
  TaskPrimaryActionButton,
  ActionType,
} from "@/SharedComponents/BulkActions";
import { DeleteModal } from "@/SharedComponents/Modals";
import { toast } from "@/SharedComponents/Toaster";
import { useBulkActions } from "@/Composables/useBulkActions";
import { useDefaultTasksViews } from "@/Pages/Tasks/Views";
import { useFilters } from "@/Composables/useFilters";
import { useI18n } from "vue-i18n";

const props = defineProps({
  selectedTasks: { type: Array, default: () => [] },
});

const { DefaultView } = useDefaultTasksViews();

const filterStore = useFilters("tasks");
const { activeView } = storeToRefs(filterStore);

const isArchivedView = computed(
  () => activeView.value.id === DefaultView.ARCHIVED.id
);

const linkedBuildingIds = computed(() => {
  const buildingIds = props.selectedTasks.map((task) => task.tenant?.id);
  return Array.from(new Set(buildingIds));
});

const useBulkActionsStore = useBulkActions(
  "tasks",
  linkedBuildingIds,
  computed(() => props.selectedTasks)
);
const { countSelected, hasActions, steps, hints } =
  storeToRefs(useBulkActionsStore);
const { handleClearSelection } = useBulkActionsStore;

const { hasPermission, permissions } = inject("permissions");

const canEditTasks = computed(
  () =>
    !linkedBuildingIds.value.some((buildingId) =>
      hasPermission(permissions.limitedAccess, buildingId)
    )
);

const canCompleteTasks = computed(() =>
  linkedBuildingIds.value.every((buildingId) =>
    hasPermission(permissions.completeTask, buildingId)
  )
);

const { t } = useI18n();
const busy = ref(false);

const selectedTaskIds = computed(() =>
  props.selectedTasks.map((task) => task.id)
);

const routes = {
  [ActionType.DUE_DATE]: "tasks.bulk.schedule_for",
  [ActionType.PRIORITY]: "tasks.bulk.prioritize",
  [ActionType.CATEGORY]: "tasks.bulk.categorize",
  [ActionType.ASSIGNED_TO]: "tasks.bulk.assign",
  [ActionType.TAGS]: "tasks.bulk.tag",
  [ActionType.VISIBILITY]: "tasks.bulk.hide",
};

function handleSubmit({ type, payload }) {
  if (busy.value || selectedTaskIds.value.length === 0) {
    return;
  }

  router.post(
    route(routes[type]),
    {
      task_ids: selectedTaskIds.value,
      ...payload,
    },
    {
      onStart: () => {
        busy.value = true;
      },
      onSuccess: () => {
        toast.success(t("toasts.tasks.update.success", 2));
      },
      onError: () => {
        toast.error(t("toasts.tasks.update.error", 2));
      },
      onFinish: () => {
        busy.value = false;
      },
    }
  );
}

function handleUpdateStatus(type) {
  if (busy.value || selectedTaskIds.value.length === 0) {
    return;
  }

  router.post(
    route(`tasks.bulk.${type}`),
    {
      task_ids: selectedTaskIds.value,
    },
    {
      onStart: () => {
        busy.value = true;
      },
      onSuccess: () => {
        if (type === "delete" || type === "restore") {
          handleClearSelection();
        }
        toast.success(t(`toasts.tasks.${type}.success`, 2));
      },
      onError: () => {
        toast.error(t(`toasts.tasks.${type}.error`, 2));
      },
      onFinish: () => {
        busy.value = false;
      },
    }
  );
}
</script>
